import React from 'react';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Clock, Archive, Database, EyeOff } from 'lucide-react';

/**
 * Color-coded badge for a DTC's status category.
 *
 * Mirrors the typed enum the mechanic app emits in `raw_codes[].statusCategory`
 * (added in v1.60.0). For older inspections / older app versions the field
 * may be absent; we fall back to lowercasing `statusText` (legacy field) and
 * default to `'stored'` if even that isn't present.
 *
 * Visual contract:
 *   - active   → red (currently failing this drive cycle / MIL-on)
 *   - pending  → amber (detected once, will confirm on next drive cycle)
 *   - history  → slate (was stored in ECU memory; not currently failing)
 *   - stored   → blue (ECU returned a non-zero DTC with status=0x00 — common
 *                on Indian-market budget ECUs that ignore the request mask)
 *   - demoted  → gray/strikethrough (v1.61.0 — flagged as ECU memory dump
 *                or contradictory pair; backend AI will deprioritize)
 */
export function DtcStatusBadge({ category, statusText, demotedReason, className = '' }) {
  // v1.61.0: if mechanic app emitted demotedReason, that wins over category
  const c = demotedReason
    ? 'demoted'
    : (category || (statusText ? String(statusText).toLowerCase() : '') || 'stored');

  const config = {
    active: {
      label: 'Active',
      icon: AlertCircle,
      tone: 'bg-red-50 text-red-700 border-red-200',
      tip: 'Currently failing — check engine light may be on',
    },
    pending: {
      label: 'Pending',
      icon: Clock,
      tone: 'bg-amber-50 text-amber-700 border-amber-200',
      tip: 'Detected once — will confirm on next drive cycle',
    },
    history: {
      label: 'History',
      icon: Archive,
      tone: 'bg-slate-50 text-slate-700 border-slate-200',
      tip: 'Was stored — not currently failing',
    },
    stored: {
      label: 'Stored',
      icon: Database,
      tone: 'bg-blue-50 text-blue-700 border-blue-200',
      tip: 'ECU has the code stored — status not specified',
    },
    demoted: {
      label: demotedReason === 'contradictory_pair' ? 'Stale (Conflict)' : 'Stale (Memory)',
      icon: EyeOff,
      tone: 'bg-gray-100 text-gray-500 border-gray-300 line-through',
      tip:
        demotedReason === 'contradictory_pair'
          ? 'Logically impossible pair (e.g., Lean+Rich on same bank) — likely uncleared memory'
          : 'Part of an ECU memory dump (15+ stored codes from same module) — likely historical, not active',
    },
  };

  const { label, icon: Icon, tone, tip } = config[c] || config.stored;

  return (
    <Badge
      variant="outline"
      className={`text-[10px] px-1.5 py-0 ${tone} ${className}`}
      title={tip}
      data-testid={`dtc-status-badge-${c}`}
    >
      <Icon className="h-2.5 w-2.5 mr-1" />
      {label}
    </Badge>
  );
}

/**
 * Aggregate summary chip showing per-category counts.
 * Renders nothing if all four counts are zero.
 *
 * Usage:
 *   <DtcStatusSummary codes={fault.raw_codes} />
 */
export function DtcStatusSummary({ codes = [], className = '' }) {
  const counts = { active: 0, pending: 0, history: 0, stored: 0, demoted: 0 };
  for (const c of codes) {
    if (c?.demotedReason || c?.confidenceTier === 'demoted') {
      counts.demoted += 1;
      continue;
    }
    const cat = (
      c?.statusCategory ||
      (c?.statusText ? String(c.statusText).toLowerCase() : '') ||
      'stored'
    );
    if (counts[cat] !== undefined) counts[cat] += 1;
    else counts.stored += 1;
  }
  const total = counts.active + counts.pending + counts.history + counts.stored + counts.demoted;
  if (total === 0) return null;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`} data-testid="dtc-status-summary">
      {counts.active > 0 && <DtcStatusBadge category="active" /> }
      {counts.active > 0 && <span className="text-xs font-semibold text-red-700 -ml-1">{counts.active}</span>}
      {counts.pending > 0 && <DtcStatusBadge category="pending" /> }
      {counts.pending > 0 && <span className="text-xs font-semibold text-amber-700 -ml-1">{counts.pending}</span>}
      {counts.history > 0 && <DtcStatusBadge category="history" /> }
      {counts.history > 0 && <span className="text-xs font-semibold text-slate-700 -ml-1">{counts.history}</span>}
      {counts.stored > 0 && <DtcStatusBadge category="stored" /> }
      {counts.stored > 0 && <span className="text-xs font-semibold text-blue-700 -ml-1">{counts.stored}</span>}
      {counts.demoted > 0 && <DtcStatusBadge demotedReason="ecu_memory_dump" /> }
      {counts.demoted > 0 && <span className="text-xs font-semibold text-gray-500 -ml-1">{counts.demoted}</span>}
    </div>
  );
}
